<template>
    <div class="c-youtube-video">
        <div class="c-youtube-video__overlay" @click="playVideo" v-touch:tap="playVideo" v-show="showOverlay"></div>
        <div class="c-youtube-video__iframe" :id="`${uniqId}_player`"></div>
    </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { EventBus } from '../helpers/bus';
import { isAttachedToDom } from '../helpers/utils';
import BaseComponent from './Base';

export enum PlayerState {
        UNSTARTED = -1,
        ENDED = 0,
        PLAYING = 1,
        PAUSED = 2,
        BUFFERING = 3,
        CUED = 5,
    }

    export interface YouTubeIframeApi {
        Player: YouTubePlayer;
        PlayerState: PlayerState;
    }

    export interface YouTubePlayer {
        prototype: YouTubePlayerInstance;
        new(id: string | Element, args: {
            height?: string;
            width?: string;
            videoId?: string;
            playerVars?: { [key: string]: any };
            events?: { [key: string]: any };
        }): YouTubePlayerInstance;
    }

    export interface YouTubePlayerInstance {
        playVideo(): void;
        stopVideo(): void;
        pauseVideo(): void;
        clearVideo(): void;
        getDuration(): number;
        getCurrentTime(): number;
        loadVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: string): void;
        cueVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: string): void;
        destroy(): void;
    }

    export interface YouTubePlayerReadyEvent {
        target: YouTubePlayerInstance;
    }

    export interface YouTubeStateChangeEvent {
        target: YouTubePlayerInstance;
        data: number;
    }

    export interface YoutubeVideoComponent {
        playVideo(): void;
        stopVideo(): void;
        pauseVideo(): void;
    }

    const ytStore = namespace('youTube');

    @Component
    export default class YoutubeVideo extends BaseComponent implements YoutubeVideoComponent {
        @ytStore.State('isScriptLoaded') protected readonly isScriptLoaded: boolean;
        @ytStore.State('isScriptReady') protected readonly isScriptReady: boolean;
        @ytStore.Action('loadScript') protected loadScript: () => void;

        @Prop({ type: String, required: true }) protected readonly src: string;
        @Prop({ type: String, default: '440px' }) protected readonly height: string;
        @Prop({ type: String, default: '100%' }) protected readonly width: string;

        protected showOverlay: boolean = true;
        protected currentTimeCounter: number = 0;
        protected videoDuration: number = 0;
        protected durationInterval: ReturnType<typeof setInterval> | null = null;
        protected player: YouTubePlayerInstance | null = null;

        public playVideo(): void {
            if (isAttachedToDom(this.$el) && this.player && this.player.playVideo) {
                this.player.playVideo();
                // play event
            }
        }

        public stopVideo(): void {
            if (isAttachedToDom(this.$el) && this.player && this.player.stopVideo) {
                this.player.stopVideo();
                // stop event
            }
        }

        public pauseVideo(): void {
            if (isAttachedToDom(this.$el) && this.player && this.player.pauseVideo) {
                this.player.pauseVideo();
                // pause event
            }
        }

        protected get loadPercentage(): number {
            if (this.videoDuration !== 0) {
                return this.currentTimeCounter / this.videoDuration * 100;
            }
            return 0;
        }

        protected onIframeReady(): void {
            this.player = new window.YT.Player(`${this.uniqId}_player`, {
                height: this.height,
                width: this.width,
                playerVars: {
                    autoplay: 0,
                    controls: 2,
                    disablekb: 1,
                    fs: 1,
                    rel: 0,
                    modestbranding: 1,
                },
                events: {
                    onReady: this.onPlayerReady,
                    onStateChange: this.onStateChange,
                },
            });
        }

        protected onPlayerReady(event: YouTubePlayerReadyEvent): void {
            event.target.cueVideoByUrl(this.src);
            this.$emit('ready', { player: event.target });
        }

        protected onStateChange(event: YouTubeStateChangeEvent): void {
            if ([PlayerState.ENDED, PlayerState.PAUSED, PlayerState.UNSTARTED].indexOf(event.data) > -1) {
                this.showOverlay = true;
                this.tearDownDurationInterval();
            }
            if (event.data === PlayerState.PLAYING) {
                this.showOverlay = false;
                this.setUpDurationInterval();
            }
            this.$emit('stateChanged', { state: event.data, player: event.target });
        }

        protected updateProgress(): void {
            if (this.player && this.player.getCurrentTime && this.player.getDuration) {
                const loadPercentagePrev = this.loadPercentage;

                this.currentTimeCounter = this.player.getCurrentTime();
                this.videoDuration = this.player.getDuration();

                if (this.loadPercentage > 0) {
                    this.$emit('progress', this.loadPercentage);
                }

                const markers: number[] = [25, 50, 75];
                markers.forEach((marker) => {
                    if (this.loadPercentage > marker && loadPercentagePrev < marker) {
                        // progress event
                    }
                });
            }
        }

        protected setUpDurationInterval(): void {
            this.updateProgress();
            this.durationInterval = setInterval(this.updateProgress, 1000);
        }

        protected tearDownDurationInterval(): void {
            this.updateProgress();
            if (this.durationInterval !== null) {
                clearInterval(this.durationInterval);
            }
        }

        protected beforeDestroy(): void {
            this.unload();

            // Stop video if slide was hidden
            if (this.$parent instanceof BaseComponent) {
                const parentUniqId: string = this.$parent.uniqId || '';
                EventBus.$off(`slideHidden:${parentUniqId}`, this.stopVideo);
            }
            // PSWP event listeners is used to play nice with PhotoSwipeWrapper component
            EventBus.$off('pswp:close', this.stopVideo);
            EventBus.$off('pswp:beforeChange', this.pauseVideo);
        }

        protected unload(): void {
            if (this.player !== null && isAttachedToDom(this.$el)) {
                try {
                    this.player.stopVideo();
                    this.player.destroy();
                } catch (e) {
                    console.info(e);
                }
            }
        }

        protected load(): void {
            this.unload();
            if (this.isScriptReady) {
                this.onIframeReady();
            } else {
                this.loadScript();
            }
        }

        protected mounted(): void {
            EventBus.$on('youTubeIframeAPIReady', this.onIframeReady);
            (this.$parent as BaseComponent).$on('attached', this.load);
            this.load();

            // Stop video if slide was hidden
            if (this.$parent instanceof BaseComponent) {
                const parentUniqId: string = this.$parent.uniqId || '';
                EventBus.$on(`slideHidden:${parentUniqId}`, this.stopVideo);
            }
            // PSWP event listeners is used to play nice with PhotoSwipeWrapper component
            EventBus.$on('pswp:close', this.stopVideo);
            EventBus.$on('pswp:beforeChange', this.pauseVideo);
        }
    }
</script>
